import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ClosingStageOption,
  ContractPaymentCalendarRow,
  ContractPaymentCalendarWrite,
  ContractStage,
  Employee,
  getClosingStages,
  getContractPaymentCalendar,
  getContractStages,
  getEmployees,
  replaceContractPaymentCalendar,
} from '../services/api';
import {
  displayContractSumDraft,
  formatContractSumDisplay,
  formatDate,
  normalizeContractSumDraft,
  parseContractSumInput,
} from '../utils/table';
import { ClosingStatusIcon } from './ClosingStatusIcon';
import './ContractPaymentCalendarTable.css';

type BindKind = 'date' | 'event';
type EventKind = 'closing_stage' | 'work_stage' | 'free';

interface CalendarDraft {
  key: string;
  id: number | null;
  amountDraft: string;
  bindKind: BindKind;
  eventKind: EventKind;
  paymentDate: string;
  daysAfter: string;
  closingStageId: number | null;
  contractStageId: number | null;
  event: string;
  responsibleId: number | null;
  responsibleName: string;
}

interface ContractPaymentCalendarTableProps {
  contractId: number;
  contractAmount: number;
  paidAmount?: number;
  canEdit: boolean;
  disabled?: boolean;
  collapsed?: boolean;
  indentLevel?: 1 | 2;
  onRegisterPersist?: (contractId: number, persist: (() => Promise<void>) | null) => void;
}

const FREE_EVENT_VALUE = 'free';
const RECEIPTS_COL_COUNT = 7;

function newDraftKey(): string {
  return `d-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function emptyDraft(): CalendarDraft {
  return {
    key: newDraftKey(),
    id: null,
    amountDraft: '',
    bindKind: 'date',
    eventKind: 'closing_stage',
    paymentDate: '',
    daysAfter: '0',
    closingStageId: null,
    contractStageId: null,
    event: '',
    responsibleId: null,
    responsibleName: '',
  };
}

function fromServerRow(row: ContractPaymentCalendarRow): CalendarDraft {
  const hasDate = Boolean(row.payment_date);
  const hasWorkStage = row.contract_stage_id != null;
  const hasStage = row.closing_stage_id != null;
  return {
    key: `s-${row.id}`,
    id: row.id,
    amountDraft: normalizeContractSumDraft(String(row.amount ?? '')),
    bindKind: hasDate ? 'date' : 'event',
    eventKind: hasWorkStage ? 'work_stage' : hasStage ? 'closing_stage' : 'free',
    paymentDate: row.payment_date ?? '',
    daysAfter: row.days_after != null ? String(row.days_after) : '0',
    closingStageId: row.closing_stage_id ?? null,
    contractStageId: row.contract_stage_id ?? null,
    event: row.event ?? '',
    responsibleId: row.responsible_id ?? null,
    responsibleName: row.responsible_name ?? '',
  };
}

function parseDaysAfter(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  if (!/^\d+$/.test(trimmed)) return undefined;
  return Number(trimmed);
}

function isDraftEmpty(draft: CalendarDraft): boolean {
  return (
    !draft.amountDraft.trim()
    && !draft.paymentDate
    && (draft.daysAfter.trim() === '' || draft.daysAfter.trim() === '0')
    && draft.closingStageId == null
    && draft.contractStageId == null
    && !draft.event.trim()
    && draft.responsibleId == null
  );
}

function isDraftComplete(draft: CalendarDraft): boolean {
  const amount = parseContractSumInput(draft.amountDraft);
  if (amount == null) return false;
  if (draft.bindKind === 'date') {
    return Boolean(draft.paymentDate);
  }
  if (parseDaysAfter(draft.daysAfter) == null) return false;
  if (draft.eventKind === 'closing_stage') {
    return draft.closingStageId != null;
  }
  if (draft.eventKind === 'work_stage') {
    return draft.contractStageId != null;
  }
  return Boolean(draft.event.trim()) && draft.responsibleId != null;
}

function toWritePayload(draft: CalendarDraft): ContractPaymentCalendarWrite {
  const amount = parseContractSumInput(draft.amountDraft) ?? 0;
  if (draft.bindKind === 'date') {
    return {
      amount,
      payment_date: draft.paymentDate,
      days_after: null,
      closing_stage_id: null,
      contract_stage_id: null,
      event: null,
      responsible_id: null,
    };
  }
  const daysAfter = parseDaysAfter(draft.daysAfter) ?? 0;
  if (draft.eventKind === 'closing_stage') {
    return {
      amount,
      payment_date: null,
      days_after: daysAfter,
      closing_stage_id: draft.closingStageId,
      contract_stage_id: null,
      event: null,
      responsible_id: null,
    };
  }
  if (draft.eventKind === 'work_stage') {
    return {
      amount,
      payment_date: null,
      days_after: daysAfter,
      closing_stage_id: null,
      contract_stage_id: draft.contractStageId,
      event: null,
      responsible_id: null,
    };
  }
  return {
    amount,
    payment_date: null,
    days_after: daysAfter,
    closing_stage_id: null,
    contract_stage_id: null,
    event: draft.event.trim(),
    responsible_id: draft.responsibleId,
  };
}

function formatClosingStageLabel(stage: ClosingStageOption): string {
  if (stage.closing_level === 3) {
    return `Закрытие работ ${stage.name}`;
  }
  return `Компания - ${stage.name}`;
}

function formatWorkStageLabel(stage: ContractStage): string {
  return `Закрытие этапа №${stage.stage_number} - ${stage.name}`;
}

function formatEventLabel(
  draft: CalendarDraft,
  stages: ClosingStageOption[],
  workStages: ContractStage[],
): string {
  if (draft.bindKind === 'date') {
    return `Фикс. дата: ${formatDate(draft.paymentDate)}`;
  }
  const days = parseDaysAfter(draft.daysAfter);
  const daysLabel = days == null ? '—' : `через ${days} дн.`;
  if (draft.eventKind === 'closing_stage') {
    const stage = stages.find((item) => item.id === draft.closingStageId);
    const stageLabel = stage ? formatClosingStageLabel(stage) : 'этап';
    return `${daysLabel} после «${stageLabel}»`;
  }
  if (draft.eventKind === 'work_stage') {
    const stage = workStages.find((item) => item.id === draft.contractStageId);
    const stageLabel = stage ? formatWorkStageLabel(stage) : 'этап работ';
    return `${daysLabel} после «${stageLabel}»`;
  }
  const event = draft.event.trim() || 'событие';
  return `${daysLabel} после «${event}»`;
}

function coverageStatus(amount: number, remaining: number): {
  status: 'closed' | 'partial' | 'open';
  remaining: number;
} {
  if (amount <= 0.005) {
    return { status: remaining > 0.005 ? 'closed' : 'open', remaining };
  }
  if (remaining >= amount - 0.005) {
    return { status: 'closed', remaining: remaining - amount };
  }
  if (remaining > 0.005) {
    return { status: 'partial', remaining: 0 };
  }
  return { status: 'open', remaining: 0 };
}

const ContractPaymentCalendarTable: React.FC<ContractPaymentCalendarTableProps> = ({
  contractId,
  contractAmount,
  paidAmount = 0,
  canEdit,
  disabled = false,
  collapsed = false,
  indentLevel = 1,
  onRegisterPersist,
}) => {
  const [rows, setRows] = useState<CalendarDraft[]>([]);
  const [stages, setStages] = useState<ClosingStageOption[]>([]);
  const [workStages, setWorkStages] = useState<ContractStage[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const persistSeq = useRef(0);
  const lastCompleteSig = useRef('');
  const rowsRef = useRef<CalendarDraft[]>([]);
  const loadingRef = useRef(true);
  const loadFailedRef = useRef(false);
  rowsRef.current = rows;

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    loadingRef.current = true;
    setError('');
    try {
      const [calendar, stageRows, workStageRows, employeeRows] = await Promise.all([
        getContractPaymentCalendar(contractId),
        getClosingStages().catch(() => [] as ClosingStageOption[]),
        getContractStages(contractId).catch(() => [] as ContractStage[]),
        getEmployees().catch(() => [] as Employee[]),
      ]);
      const drafts = calendar.map(fromServerRow);
      setRows(drafts);
      setStages(stageRows);
      setWorkStages(workStageRows);
      setEmployees(employeeRows);
      lastCompleteSig.current = JSON.stringify(drafts.filter(isDraftComplete).map(toWritePayload));
      loadFailedRef.current = false;
    } catch (e) {
      loadFailedRef.current = true;
      setError(e instanceof Error ? e.message : 'Не удалось загрузить график поступлений');
      setRows([]);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    void load();
  }, [load]);

  const persist = useCallback(async (): Promise<void> => {
    if (loadingRef.current) return;
    if (loadFailedRef.current) {
      const message = 'Не удалось загрузить график поступлений';
      setError(message);
      throw new Error(message);
    }
    const next = rowsRef.current;
    const incomplete = next.filter((row) => !isDraftEmpty(row) && !isDraftComplete(row));
    if (incomplete.length > 0) {
      const message = 'Заполните все платежи графика поступлений';
      setError(message);
      throw new Error(message);
    }
    const complete = next.filter(isDraftComplete);
    const signature = JSON.stringify(complete.map(toWritePayload));
    if (signature === lastCompleteSig.current) {
      setRows(complete);
      setError('');
      return;
    }
    const seq = ++persistSeq.current;
    setSaving(true);
    try {
      const saved = await replaceContractPaymentCalendar(contractId, complete.map(toWritePayload));
      if (seq !== persistSeq.current) return;
      lastCompleteSig.current = JSON.stringify(saved.map((row) => toWritePayload(fromServerRow(row))));
      setRows(saved.map(fromServerRow));
      setError('');
    } catch (e) {
      if (seq !== persistSeq.current) return;
      const message = e instanceof Error ? e.message : 'Не удалось сохранить график поступлений';
      setError(message);
      throw new Error(message);
    } finally {
      if (seq === persistSeq.current) setSaving(false);
    }
  }, [contractId]);

  useEffect(() => {
    if (!onRegisterPersist) return;
    onRegisterPersist(contractId, persist);
    return () => onRegisterPersist(contractId, null);
  }, [contractId, persist, onRegisterPersist]);

  const applyRows = useCallback((updater: (prev: CalendarDraft[]) => CalendarDraft[]): void => {
    setRows((prev) => updater(prev));
  }, []);

  const updateRow = useCallback((key: string, patch: Partial<CalendarDraft>): void => {
    applyRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }, [applyRows]);

  const handleAdd = useCallback((): void => {
    applyRows((prev) => [...prev, emptyDraft()]);
  }, [applyRows]);

  const handleDelete = useCallback((key: string): void => {
    applyRows((prev) => prev.filter((row) => row.key !== key));
  }, [applyRows]);

  const distributed = useMemo(
    () => rows.reduce((sum, row) => sum + (parseContractSumInput(row.amountDraft) ?? 0), 0),
    [rows],
  );
  const remainder = contractAmount - distributed;
  const remainderKind =
    Math.abs(remainder) < 0.005 ? 'ok' : remainder < 0 ? 'over' : 'left';

  const paidStatuses = useMemo(() => {
    let remaining = paidAmount;
    return rows.map((row) => {
      const amount = parseContractSumInput(row.amountDraft) ?? 0;
      const result = coverageStatus(amount, remaining);
      remaining = result.remaining;
      return result.status;
    });
  }, [rows, paidAmount]);

  const employeeOptions = useMemo(() => {
    const byId = new Map(employees.map((emp) => [emp.id, emp]));
    for (const row of rows) {
      if (row.responsibleId != null && !byId.has(row.responsibleId)) {
        byId.set(row.responsibleId, {
          id: row.responsibleId,
          username: '',
          full_name: `Сотрудник #${row.responsibleId}`,
        });
      }
    }
    return [...byId.values()].sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru'));
  }, [employees, rows]);

  const indentClass =
    indentLevel === 2
      ? 'contract-detail__receipts-stage-indent contract-detail__receipts-stage-indent--ds'
      : 'contract-detail__receipts-stage-indent';
  const stageRowClass = [
    'contract-detail__receipts-stage-row',
    collapsed ? 'contract-detail__receipts-stage-row--collapsed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const renderBind = (row: CalendarDraft, index: number): React.ReactNode => {
    if (!canEdit) {
      return (
        <div className="contract-detail__calendar-bind-view">
          <span>{formatEventLabel(row, stages, workStages)}</span>
          {row.bindKind === 'event' && row.eventKind === 'free' && (row.responsibleName || row.responsibleId != null) ? (
            <span className="contract-detail__calendar-responsible">
              {employeeOptions.find((emp) => emp.id === row.responsibleId)?.full_name
                ?? row.responsibleName
                ?? `Сотрудник #${row.responsibleId}`}
            </span>
          ) : null}
        </div>
      );
    }

    return (
      <div className="contract-detail__calendar-bind-edit">
        <select
          className="contractor-detail__field contract-detail__calendar-select"
          value={row.bindKind}
          disabled={disabled}
          onChange={(e) => {
            const bindKind = e.target.value as BindKind;
            updateRow(row.key, bindKind === 'date'
              ? {
                  bindKind,
                  closingStageId: null,
                  contractStageId: null,
                  event: '',
                  daysAfter: '0',
                  responsibleId: null,
                }
              : {
                  bindKind,
                  paymentDate: '',
                  daysAfter: row.daysAfter || '0',
                });
          }}
          aria-label={`Тип привязки платежа ${index + 1}`}
        >
          <option value="date">Дата</option>
          <option value="event">Событие</option>
        </select>
        {row.bindKind === 'date' ? (
          <input
            type="date"
            className="contractor-detail__field task-detail__cell-input contract-detail__calendar-date"
            value={row.paymentDate}
            disabled={disabled}
            onChange={(e) => updateRow(row.key, { paymentDate: e.target.value })}
            aria-label={`Дата платежа ${index + 1}`}
          />
        ) : (
          <>
            <label className="contract-detail__calendar-days">
              через
              <input
                type="text"
                inputMode="numeric"
                className="contractor-detail__field task-detail__cell-input contract-detail__calendar-days-input"
                value={row.daysAfter}
                disabled={disabled}
                onChange={(e) => updateRow(row.key, {
                  daysAfter: e.target.value.replace(/\D/g, ''),
                })}
                aria-label={`Дней после события, платеж ${index + 1}`}
              />
              дн.
            </label>
            <select
              className="contractor-detail__field contract-detail__calendar-select"
              value={
                row.eventKind === 'free'
                  ? FREE_EVENT_VALUE
                  : row.eventKind === 'work_stage' && row.contractStageId != null
                    ? `work:${row.contractStageId}`
                    : row.closingStageId != null
                      ? `stage:${row.closingStageId}`
                      : ''
              }
              disabled={disabled}
              onChange={(e) => {
                const value = e.target.value;
                if (value === FREE_EVENT_VALUE) {
                  updateRow(row.key, {
                    eventKind: 'free',
                    closingStageId: null,
                    contractStageId: null,
                  });
                  return;
                }
                if (value.startsWith('work:')) {
                  updateRow(row.key, {
                    eventKind: 'work_stage',
                    contractStageId: Number(value.slice(5)),
                    closingStageId: null,
                    event: '',
                    responsibleId: null,
                  });
                  return;
                }
                if (value.startsWith('stage:')) {
                  updateRow(row.key, {
                    eventKind: 'closing_stage',
                    closingStageId: Number(value.slice(6)),
                    contractStageId: null,
                    event: '',
                    responsibleId: null,
                  });
                  return;
                }
                updateRow(row.key, {
                  eventKind: 'closing_stage',
                  closingStageId: null,
                  contractStageId: null,
                });
              }}
              aria-label={`Событие платежа ${index + 1}`}
            >
              <option value="">Событие</option>
              {workStages.map((stage) => (
                <option key={`work-${stage.id}`} value={`work:${stage.id}`}>
                  {formatWorkStageLabel(stage)}
                </option>
              ))}
              {stages.map((stage) => (
                <option key={stage.id} value={`stage:${stage.id}`}>
                  {formatClosingStageLabel(stage)}
                </option>
              ))}
              <option value={FREE_EVENT_VALUE}>Свободное</option>
            </select>
            {row.eventKind === 'free' ? (
              <>
                <input
                  type="text"
                  className="contractor-detail__field task-detail__cell-input contract-detail__calendar-event-input"
                  value={row.event}
                  disabled={disabled}
                  onChange={(e) => updateRow(row.key, { event: e.target.value })}
                  placeholder="Событие"
                  aria-label={`Название свободного события, платеж ${index + 1}`}
                />
                <select
                  className="contractor-detail__field contract-detail__calendar-select"
                  value={row.responsibleId == null ? '' : String(row.responsibleId)}
                  disabled={disabled}
                  onChange={(e) => updateRow(row.key, {
                    responsibleId: e.target.value === '' ? null : Number(e.target.value),
                  })}
                  aria-label={`Ответственный за сигнал, платеж ${index + 1}`}
                >
                  <option value="">Ответственный</option>
                  {employeeOptions.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.full_name}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <tr className={stageRowClass}>
        <td colSpan={RECEIPTS_COL_COUNT} className={indentClass}>
          <span className="contractor-detail__muted">Загрузка графика…</span>
        </td>
      </tr>
    );
  }

  return (
    <>
      {error ? (
        <tr className={stageRowClass}>
          <td colSpan={RECEIPTS_COL_COUNT} className={indentClass}>
            <span className="contract-detail__calendar-error">{error}</span>
          </td>
        </tr>
      ) : null}
      {rows.length === 0 && !canEdit ? (
        <tr className={stageRowClass}>
          <td colSpan={RECEIPTS_COL_COUNT} className={`${indentClass} contract-detail__calendar-empty`}>
            Платежи не заданы
          </td>
        </tr>
      ) : null}
      {rows.map((row, index) => (
        <tr key={row.key} className={stageRowClass}>
          <td className={`task-detail__td-text ${indentClass}`}>
            <span className="contract-detail__receipts-stage-head">
              <span className="contract-detail__receipts-stage-num">{index + 1} платеж</span>
              {canEdit ? (
                <button
                  type="button"
                  className="contract-detail__calendar-delete"
                  onClick={() => handleDelete(row.key)}
                  disabled={disabled}
                  aria-label={`Удалить платеж ${index + 1}`}
                >
                  ×
                </button>
              ) : null}
            </span>
          </td>
          <td colSpan={2} className="contract-detail__calendar-bind">
            {renderBind(row, index)}
          </td>
          <td className="contract-detail__receipts-col-num">
            {canEdit ? (
              <input
                type="text"
                inputMode="numeric"
                className="contractor-detail__field task-detail__cell-input task-detail__cell-input--num contract-detail__receipts-amount-input"
                value={displayContractSumDraft(row.amountDraft)}
                disabled={disabled}
                onChange={(e) => updateRow(row.key, {
                  amountDraft: normalizeContractSumDraft(e.target.value),
                })}
                aria-label={`Сумма платежа ${index + 1}`}
              />
            ) : (
              formatContractSumDisplay(parseContractSumInput(row.amountDraft) ?? 0)
            )}
          </td>
          <td className="contract-detail__receipts-stage-paid">
            <ClosingStatusIcon status={paidStatuses[index]} />
          </td>
          <td />
          <td />
        </tr>
      ))}
      {remainderKind !== 'ok' && (canEdit || rows.length > 0) ? (
        <tr className={`${stageRowClass} contract-detail__receipts-stage-row--remainder`}>
          <td className={`task-detail__td-text ${indentClass}`}>
            <span className="contract-detail__receipts-stage-num">
              {remainderKind === 'over' ? 'превышение' : 'не распределено'}
            </span>
          </td>
          <td colSpan={2} />
          <td className="contract-detail__receipts-col-num contract-detail__calendar-remainder-amount">
            {formatContractSumDisplay(Math.abs(remainder))}
          </td>
          <td />
          <td />
          <td />
        </tr>
      ) : null}
      {canEdit ? (
        <tr className={`${stageRowClass} contract-detail__receipts-stage-row--foot`}>
          <td colSpan={RECEIPTS_COL_COUNT} className={indentClass}>
            <div className="contract-detail__calendar-footer">
              <button
                type="button"
                className="contract-detail__calendar-add"
                onClick={handleAdd}
                disabled={disabled}
              >
                + Платеж
              </button>
              {saving ? (
                <span className="contract-detail__calendar-totals">сохранение…</span>
              ) : null}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
};

export default ContractPaymentCalendarTable;
